import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DisclaimerModal from '../components/DisclaimerModal';
import { useBeautyProduct } from '../../../beauty-shared/context/BeautyProductContext';
import { usesLooksCaptureChrome } from '../../../beauty-shared/config/saasModules';
import { useLooksPageCopy } from '../../looks/hooks/useLooksPageCopy';
import '../styles/CapturePreviewPage.css';
import NaturalsCameraSelect from '../../shared/EnterpriseCameraSelect';
import { parseCameraSelectValue, resolveFaceCameraSelectValue } from '../../shared/cameraSelectValues';
import {
  listMicroscopeVideoInputs,
  openBuiltinFaceCameraStream,
} from '../../shared/usbMicroscopeCamera';

const FACING_USER = 'user';
const FACING_ENV = 'environment';

export default function CapturePreviewPage() {
  const navigate = useNavigate();
  const { appPath, appBase } = useBeautyProduct();
  const { title: pageTitle, cta: pageCta } = useLooksPageCopy();
  const isLooks = usesLooksCaptureChrome(appBase);
  const { mode } = useParams();
  const isCameraMode = mode === 'camera';

  const viewportRef = useRef(null);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const [facingMode, setFacingMode] = useState(FACING_USER);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const [sourceImage, setSourceImage] = useState('');
  const [capturedPreview, setCapturedPreview] = useState('');
  const [flashAnim, setFlashAnim] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const startCameraFromSelect = useCallback(
    async (rawValue) => {
      stopStream();
      setCameraError('');
      const parsed = parseCameraSelectValue(rawValue);
      if (parsed.kind === 'none') {
        setCameraActive(false);
        return;
      }

      try {
        let stream;
        if (parsed.kind === 'device') {
          setSelectedDeviceId(parsed.deviceId);
          setFacingMode(FACING_USER);
          stream = await openBuiltinFaceCameraStream({
            deviceId: parsed.deviceId,
            facing: 'user',
            timeoutMs: 15000,
            width: 1920,
            height: 1080,
          });
        } else {
          setSelectedDeviceId('');
          const facing = parsed.facing === 'rear' ? FACING_ENV : FACING_USER;
          setFacingMode(facing);
          stream = await openBuiltinFaceCameraStream({
            facing,
            timeoutMs: 15000,
            width: 1920,
            height: 1080,
          });
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCameraActive(true);
      } catch {
        setCameraError('Could not access camera. Check camera permission and try again.');
        setCameraActive(false);
      }
    },
    [stopStream],
  );

  const cameraSelectValue = resolveFaceCameraSelectValue({
    deviceId: selectedDeviceId,
    facing: facingMode === FACING_ENV ? 'rear' : 'front',
  });

  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  useEffect(() => {
    if (!isCameraMode) {
      stopStream();
    }
  }, [isCameraMode, stopStream]);

  const refreshVideoDevices = useCallback(async () => {
    try {
      const cams = await listMicroscopeVideoInputs();
      setVideoDevices(cams);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isCameraMode || !disclaimerAccepted) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const cams = await listMicroscopeVideoInputs();
        if (!cancelled) setVideoDevices(cams);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCameraMode, disclaimerAccepted]);

  const handleDisclaimerAccept = async () => {
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);

    if (isCameraMode) {
      const initial = resolveFaceCameraSelectValue({
        deviceId: selectedDeviceId,
        facing: facingMode === FACING_ENV ? 'rear' : 'front',
      });
      await startCameraFromSelect(initial || 'auto:front');
      return;
    }

    fileInputRef.current?.click();
  };

  const handleDisclaimerDecline = () => {
    navigate(appPath('/capture'));
  };

  const handleCameraSelectChange = async (value) => {
    await startCameraFromSelect(value);
  };

  /** Full sensor frame — no face-zoom crop (guide overlays are UI-only). */
  const captureFromVideo = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    // Mirror front camera so the saved image matches what the guest saw.
    if (facingMode === FACING_USER) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    setFlashAnim(true);
    setTimeout(() => setFlashAnim(false), 260);
    setCapturedPreview(canvas.toDataURL('image/jpeg', 0.95));
    stopStream();
  };

  const onUploadSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setSourceImage(e.target.result);
      setCapturedPreview('');
    };
    reader.readAsDataURL(file);
  };

  const handleUsePhoto = () => {
    const selectedImage = isCameraMode ? capturedPreview : sourceImage;
    if (!selectedImage) return;
    navigate(appPath('/studio'), { state: { image: selectedImage } });
  };

  const retake = async () => {
    setCapturedPreview('');
    if (isCameraMode) {
      await startCameraFromSelect(cameraSelectValue || 'auto:front');
      return;
    }
    setSourceImage('');
    fileInputRef.current?.click();
  };

  const heading = isCameraMode
    ? (isLooks ? `${pageTitle} · Camera` : 'Camera Preview')
    : (isLooks ? `${pageTitle} · Upload` : 'Upload Preview');
  const continueLabel = isLooks ? pageCta : 'Confirm and Continue';

  return (
    <div className="preview-root">
      <DisclaimerModal
        open={showDisclaimer && !disclaimerAccepted}
        mode={isCameraMode ? 'camera' : 'file'}
        onAccept={handleDisclaimerAccept}
        onDecline={handleDisclaimerDecline}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onUploadSelected}
      />

      <div className="preview-title-wrap">
        <h1>{heading}</h1>
      </div>

      <div className={`preview-viewport ${flashAnim ? 'flash' : ''}`} ref={viewportRef}>
        {capturedPreview ? (
          <img src={capturedPreview} alt="Captured preview" className="preview-media" />
        ) : isCameraMode ? (
          <>
            <video
              ref={videoRef}
              className={`preview-media ${facingMode === FACING_USER ? 'mirror' : ''}`}
              autoPlay
              playsInline
              muted
            />
            {cameraError && <div className="preview-error">{cameraError}</div>}
            {!capturedPreview && (
              <div className="preview-camera-dock">
                <NaturalsCameraSelect
                  label="Camera"
                  value={cameraSelectValue}
                  devices={videoDevices}
                  onChange={handleCameraSelectChange}
                  onRefresh={refreshVideoDevices}
                  disabled={!disclaimerAccepted}
                />
              </div>
            )}
          </>
        ) : sourceImage ? (
          <img src={sourceImage} alt="Upload source" className="preview-media" />
        ) : (
          <div className="preview-placeholder">
            <p>Select an image to continue</p>
          </div>
        )}

      </div>

      <div className="preview-controls">
        {isCameraMode && capturedPreview ? (
          <>
            <button className="ctrl-btn secondary" onClick={retake}>Retake</button>
            <button className="ctrl-btn primary" onClick={handleUsePhoto}>{continueLabel}</button>
          </>
        ) : isCameraMode ? (
          <>
            <button className="ctrl-btn capture" onClick={captureFromVideo} disabled={!cameraActive}>Capture</button>
          </>
        ) : (
          <>
            <button className="ctrl-btn secondary" onClick={() => fileInputRef.current?.click()}>
              Select Image
            </button>
            <button className="ctrl-btn primary" onClick={handleUsePhoto} disabled={!sourceImage}>
              {continueLabel}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
